import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const THEME = '#26256b';
const THEME_LIGHT = '#3230a0';
const ACCENT = '#4f9cf9';

const NewPasswordModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      setError('Password must contain uppercase, lowercase, number, and special character');
      return;
    }

    onSubmit(newPassword);
  };

  const handleClose = () => {
    if (!loading) {
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      onClose();
    }
  };

  const inputStyle = (field) => ({
    width: '100%',
    padding: '12px 44px 12px 14px',
    border: `1.5px solid ${focusedField === field ? THEME : '#d1d5db'}`,
    borderRadius: 10,
    outline: 'none',
    fontSize: 14,
    color: '#1f2937',
    background: '#fff',
    transition: 'border-color 0.2s',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)' }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            style={{
              position: 'relative',
              background: '#fff',
              borderRadius: 18,
              boxShadow: '0 8px 40px rgba(38,37,107,0.18)',
              width: '100%',
              maxWidth: 440,
              padding: 36,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              disabled={loading}
              style={{
                position: 'absolute', top: 14, right: 14,
                background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                color: '#9ca3af', opacity: loading ? 0.5 : 1, padding: 6, borderRadius: 6,
              }}
            >
              <XMarkIcon style={{ width: 20, height: 20 }} />
            </button>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                margin: '0 auto 16px',
                width: 60, height: 60,
                background: THEME,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 16px ${THEME}55`,
              }}>
                <LockClosedIcon style={{ width: 28, height: 28, color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: '0 0 6px' }}>
                Set New Password
              </h2>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                Your account requires a new password. Please create a strong password.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* New Password */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('newPassword')}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle('newPassword')}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={loading}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0,
                    }}
                  >
                    {showNewPassword
                      ? <EyeSlashIcon style={{ width: 18, height: 18 }} />
                      : <EyeIcon style={{ width: 18, height: 18 }} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle('confirmPassword')}
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0,
                    }}
                  >
                    {showConfirmPassword
                      ? <EyeSlashIcon style={{ width: 18, height: 18 }} />
                      : <EyeIcon style={{ width: 18, height: 18 }} />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div style={{
                background: `${THEME}0d`,
                border: `1px solid ${THEME}33`,
                borderRadius: 8,
                padding: '10px 14px',
              }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: THEME, margin: '0 0 6px' }}>
                  Password Requirements:
                </p>
                <ul style={{ fontSize: 12, color: THEME_LIGHT, margin: 0, padding: 0, listStyle: 'none', lineHeight: 1.8 }}>
                  <li>• At least 8 characters long</li>
                  <li>• Contains uppercase letter (A-Z)</li>
                  <li>• Contains lowercase letter (a-z)</li>
                  <li>• Contains number (0-9)</li>
                  <li>• Contains special character (!@#$%^&*)</li>
                </ul>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#fef2f2',
                    border: '1.5px solid #fca5a5',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="#ef4444" style={{ marginTop: 1, flexShrink: 0 }}>
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span style={{ fontSize: 13, color: '#dc2626', lineHeight: 1.45 }}>{error}</span>
                </motion.div>
              )}

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px 16px',
                    border: `1.5px solid #d1d5db`,
                    background: '#fff',
                    color: '#374151',
                    borderRadius: 10,
                    fontWeight: 500,
                    fontSize: 14,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1,
                    fontFamily: "'Inter', sans-serif",
                    transition: 'background 0.15s',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px 16px',
                    background: THEME,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    boxShadow: `0 4px 14px ${THEME}55`,
                    fontFamily: "'Inter', sans-serif",
                    transition: 'opacity 0.15s',
                  }}
                >
                  {loading ? 'Setting Password...' : 'Set Password'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NewPasswordModal;
